import {
  PaintDamageCountByAlarmLevelQuery,
  usePaintDamageCountByAlarmLevelQuery,
} from '@/__generated__/graphql';
import { applyFilters as applyFiltersState } from '@/components/Analysis/state';
import { useWindowSize } from '@/hooks/useWindowSize';
import { PD_ALARM_LEVEL_COLOR_MAP } from '@/theme/colors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { PieChartWidget } from '../shared/PieChartWidget';
import { getAlarmLevelColors } from '../styles';
import { titleCase } from '@/utils';
import { PoiSummaryWidgetsProps } from '../StatusWidget/StatusWidget';

export const PaintDamageAlarmLevelWidget = ({
  queryInput,
  widgetWidth = '0',
}: PoiSummaryWidgetsProps) => {
  const applyFilters = useAtomValue(applyFiltersState);
  const [loading, setLoading] = useState(false);
  const [alarmLevelCount, setAlarmLevelCount] =
    useState<PaintDamageCountByAlarmLevelQuery['paintDamageCountByAlarmLevel']>();

  const screenSize = useWindowSize();
  const handleAlarmLevelData = useCallback(
    (data: PaintDamageCountByAlarmLevelQuery) => {
      if (data?.paintDamageCountByAlarmLevel) {
        setAlarmLevelCount(data?.paintDamageCountByAlarmLevel);
      }
    },
    [setAlarmLevelCount]
  );

  const { loading: alarmLevelCountLoading, refetch } = usePaintDamageCountByAlarmLevelQuery({
    fetchPolicy: 'network-only',
    variables: { input: queryInput },
    skip: !applyFilters,
    onCompleted(data: PaintDamageCountByAlarmLevelQuery) {
      handleAlarmLevelData(data);
    },
    onError() {
      setAlarmLevelCount([]);
    },
  });

  useEffect(() => {
    if (applyFilters) {
      refetch().then(({ data }) => handleAlarmLevelData(data));
    }
  }, [applyFilters, handleAlarmLevelData, refetch]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(alarmLevelCountLoading);
    }, 800);
    return () => clearTimeout(timer);
  }, [alarmLevelCountLoading]);

  const legendKeys = useMemo(() => {
    return Object.keys(PD_ALARM_LEVEL_COLOR_MAP).map((key) => titleCase(key.replaceAll('_', ' ')));
  }, []);

  const data = useMemo(() => {
    if (alarmLevelCount?.length === 0 || alarmLevelCount?.every((item) => item?.count === null)) {
      return [{ key: 'N/A', count: 1 }];
    }
    return alarmLevelCount
      ?.map((item) => ({
        count: item?.count,
        key: `Alarm level ${item?.key}`,
      }))
      .sort((a, b) => {
        return legendKeys.indexOf(a.key ?? '') - legendKeys.indexOf(b.key ?? '');
      });
  }, [alarmLevelCount, legendKeys]);

  const size = useMemo(() => {
    const { width } = screenSize;
    if (width && width <= 1728) {
      return {
        outerRadius: 52,
        innerRadius: 43,
      };
    }
    return {
      outerRadius: 75,
      innerRadius: 62,
    };
  }, [screenSize]);

  const scale = useMemo(() => {
    const { width } = screenSize;
    if (width && width <= 1728) {
      return 0.7;
    }
    return 1;
  }, [screenSize]);

  const { highCount, highText } = useMemo(() => {
    return (
      legendKeys
        .map((key) => ({
          highText: key,
          highCount: data?.find((item) => item.key === key)?.count ?? 0,
        }))
        .find((key) => key.highCount) ?? { highCount: 0, highText: legendKeys[0] }
    );
  }, [data, legendKeys]);

  return (
    <PieChartWidget
      widgetWidth={widgetWidth}
      loading={loading}
      chartScale={scale}
      data={data}
      chartSize={size}
      colorMapper={getAlarmLevelColors}
      title="Alarm levels"
      centerText={highText}
      centerValue={highCount}
      legendKeys={legendKeys}
    />
  );
};
